// components/CompactWeatherDisplay.jsx - Compact Inline-Editable Weather Display
import React from 'react'
import { Thermometer, Wind, Droplets, Pencil, AlertTriangle, Loader2 } from 'lucide-react'

export default function CompactWeatherDisplay({
  weather,
  warnings,
  formData,
  updateField,
  loading,
  onRefresh,
  editing,
  onToggleEdit
}) {

  if (editing) {
    // Expanded edit mode
    return (
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Weather Conditions (Optional)</h3>
          <button
            onClick={onToggleEdit}
            className="text-sm text-arbor-600 hover:text-arbor-700"
          >
            Done
          </button>
        </div>

        {/* Show warnings if any */}
        {warnings && warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map((warning, idx) => (
              <div key={idx} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-yellow-800">{warning}</span>
              </div>
            ))}
          </div>
        )}

        {/* Manual entry fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">Temperature (°F)</label>
            <input
              type="number"
              value={formData.temperature}
              onChange={(e) => updateField('temperature', e.target.value)}
              className="form-input"
              placeholder="e.g., 72"
            />
          </div>

          <div>
            <label className="form-label">Humidity (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.humidity}
              onChange={(e) => updateField('humidity', e.target.value)}
              className="form-input"
              placeholder="e.g., 65"
            />
          </div>

          <div>
            <label className="form-label">Wind Speed (mph)</label>
            <input
              type="number"
              value={formData.windSpeed}
              onChange={(e) => updateField('windSpeed', e.target.value)}
              className="form-input"
              placeholder="e.g., 5"
            />
          </div>

          <div>
            <label className="form-label">Wind Direction</label>
            <select
              value={formData.windDirection}
              onChange={(e) => updateField('windDirection', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="N">North</option>
              <option value="NE">Northeast</option>
              <option value="E">East</option>
              <option value="SE">Southeast</option>
              <option value="S">South</option>
              <option value="SW">Southwest</option>
              <option value="W">West</option>
              <option value="NW">Northwest</option>
              <option value="Calm">Calm</option>
              <option value="Variable">Variable</option>
            </select>
          </div>
        </div>

        {/* Refresh button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-sm text-arbor-600 hover:text-arbor-700 disabled:opacity-50 flex items-center gap-1"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Refresh Weather Data'
            )}
          </button>
        )}
      </div>
    )
  }

  // Compact display mode
  const hasWeatherData = formData.temperature || formData.humidity || formData.windSpeed

  return (
    <div>
      <button
        onClick={onToggleEdit}
        className="card group w-full text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Weather</span>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : hasWeatherData ? (
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {formData.temperature && (
                  <div className="flex items-center gap-1">
                    <Thermometer className="w-4 h-4" />
                    <span>{formData.temperature}°F</span>
                  </div>
                )}
                {formData.windSpeed && (
                  <div className="flex items-center gap-1">
                    <Wind className="w-4 h-4" />
                    <span>{formData.windSpeed} mph {formData.windDirection}</span>
                  </div>
                )}
                {formData.humidity && (
                  <div className="flex items-center gap-1">
                    <Droplets className="w-4 h-4" />
                    <span>{formData.humidity}%</span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-sm text-gray-400">Click to add weather data</span>
            )}
          </div>

          <Pencil className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Always show warnings prominently even in compact mode */}
        {warnings && warnings.length > 0 && (
          <div className="mt-3 space-y-2">
            {warnings.map((warning, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <span className="text-yellow-800">{warning}</span>
              </div>
            ))}
          </div>
        )}
      </button>
    </div>
  )
}
